import { useUserPreference } from "@coworker/app/src/hooks/useProfilePreferencesQuery";
import { SearchResultsSkeleton } from "@coworker/apprestructured/src/scan-and-search/components/searchResults/SearchResultsSkeleton/SearchResultsSkeleton";
import { useCallSikSearchBox as useCallSikSearchBoxCoworker } from "@coworker/apprestructured/src/scan-and-search/hooks/search/useCallSikSearchBoxCoworker";
import { useSearchItems } from "@coworker/apprestructured/src/scan-and-search/hooks/search/useSearchItems";
import { SearchItemCardRenderType } from "@coworker/apprestructured/src/scan-and-search/types/SearchItemCardRenderType";
import { useItemsStock } from "@coworker/apprestructured/src/shared/hooks/item/useItemsStock";
import profilePreferences from "@coworker/enums/profilePreferences";
import { Dispatch, SetStateAction, useEffect, useMemo } from "react";
import { Item } from "../../../shared/types/item";
import { checkSikSearchQueryForMultipleEntries } from "../../helpers/searchUtils";
import { SearchResultsPresentation } from "./SearchResultsPresentation";

interface SearchResultsProps {
  searchQuery: string;
  setSearchQuery: Dispatch<SetStateAction<string>>;
  shouldHideSPRItems: boolean;
  shouldHideLocalCombinationItems: boolean;
  handleItemCardClick?: (item: Item) => void;
  itemCardRenderType?: SearchItemCardRenderType;
  multiSelect?: boolean;
  selectedItemNos?: string[];
  onToggleItemSelect?: (item: Item) => void;
  onHasContentChange?: (hasContent: boolean) => void;
}

export const SearchResults = ({
  searchQuery,
  setSearchQuery,
  shouldHideSPRItems,
  shouldHideLocalCombinationItems,
  handleItemCardClick = () => {},
  itemCardRenderType = SearchItemCardRenderType.link,
  multiSelect = false,
  selectedItemNos = [],
  onToggleItemSelect,
  onHasContentChange,
}: SearchResultsProps) => {
  const shouldSkipSikSearch =
    checkSikSearchQueryForMultipleEntries(searchQuery);

  const {
    data: searchProposals,
    isLoading: isLoadingProposals,
    isError,
  } = useCallSikSearchBoxCoworker(searchQuery, shouldSkipSikSearch);

  const {
    data: items,
    isLoading,
    fetchNextPage,
    totalItems,
    isError: isErrorFetchingItems,
  } = useSearchItems(
    searchQuery,
    shouldHideSPRItems,
    shouldHideLocalCombinationItems,
    shouldSkipSikSearch
  );

  const itemsStockQuery = useMemo(
    () => (items || []).map(({ no, type }) => `${no}:${type}`),
    [items]
  );
  const { data: itemsStock } = useItemsStock(itemsStockQuery);

  const [searchHistory, setSearchHistory] = useUserPreference<string[]>(
    profilePreferences.SEARCH_HISTORY,
    []
  );

  const isLoadingResults = isLoading || isLoadingProposals;
  // True while the skeleton is shown and once there are items or suggestions.
  const hasContent =
    isLoadingResults ||
    (items?.length ?? 0) > 0 ||
    (searchProposals?.length ?? 0) > 0;

  useEffect(() => {
    onHasContentChange?.(hasContent);
  }, [hasContent, onHasContentChange]);

  const showSearchResultsPage =
    !isError && !isErrorFetchingItems && items && searchProposals;
  if (isLoadingResults) {
    return <SearchResultsSkeleton />;
  } else if (shouldSkipSikSearch || showSearchResultsPage) {
    return (
      <SearchResultsPresentation
        proposals={searchProposals ?? []}
        items={items}
        itemsStock={itemsStock || {}}
        loading={isLoading || isLoadingProposals}
        totalCount={totalItems}
        searchQuery={searchQuery}
        handleLoadMoreClick={fetchNextPage}
        setSearchQuery={setSearchQuery}
        setSearchHistory={setSearchHistory}
        handleItemCardClick={handleItemCardClick}
        itemCardRenderType={itemCardRenderType}
        searchHistory={searchHistory}
        shouldShowNoResultsPage={shouldSkipSikSearch}
        multiSelect={multiSelect}
        selectedItemNos={selectedItemNos}
        onToggleItemSelect={onToggleItemSelect}
      />
    );
  } else {
    return null;
  }
};
